import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useState, useEffect } from "react";

import {
  useGetActivePromptQuery,
  useCreatePromptVersionMutation,
  useGetAllPromptVersionsQuery,
} from "../../store/queries/claims";
import { INPUT_SCHEMA, OUTPUT_SCHEMA } from "./schemas";

/**
 * AIPromptEditor Component
 *
 * Allows SuperAdmin users to view and edit AI analysis prompt versions.
 * Displays five editable text areas for the five prompt sections:
 * 1. Identity (role/identity description)
 * 2. Issue Summary (STEP 1-2, implant exclusion, discrepancy detection)
 * 3. Classification (STEP 3, taxonomy, deterministic rules)
 * 4. Appeal Letter (appeal letter template)
 * 5. Final Rules (final rules and closing instructions)
 *
 * Note: The JSON output schema is injected automatically by the backend
 * between the Classification and Appeal Letter sections. It is hardcoded
 * and tightly coupled to the parsing logic.
 */
function AIPromptEditor() {
  const toastRef = React.useRef<Toast>(null);
  const { data: activePrompt, isLoading, error } = useGetActivePromptQuery();
  const { data: allVersions = [], isLoading: isLoadingVersions } =
    useGetAllPromptVersionsQuery();
  const [createPromptVersion, { isLoading: isSaving }] =
    useCreatePromptVersionMutation();

  // Local state for editable prompt sections
  const [identity, setIdentity] = useState("");
  const [issueSummary, setIssueSummary] = useState("");
  const [classification, setClassification] = useState("");
  const [appealLetter, setAppealLetter] = useState("");
  const [reprocessLetter, setReprocessLetter] = useState("");
  const [finalRules, setFinalRules] = useState("");

  // Track original values to detect changes
  const [originalValues, setOriginalValues] = useState({
    identity: "",
    issueSummary: "",
    classification: "",
    appealLetter: "",
    reprocessLetter: "",
    finalRules: "",
  });

  // State for version selection and edit mode
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );
  const [isEditMode, setIsEditMode] = useState(true);

  // Sync local state with fetched active prompt or selected version
  useEffect(() => {
    if (selectedVersionId) {
      // Load selected historical version
      const selectedVersion = allVersions.find(
        (v) => v.id === selectedVersionId
      );
      if (selectedVersion) {
        setIdentity(selectedVersion.identity);
        setIssueSummary(selectedVersion.issueSummary);
        setClassification(selectedVersion.classification);
        setAppealLetter(selectedVersion.appealLetter);
        setReprocessLetter(selectedVersion.reprocessLetter || "");
        setFinalRules(selectedVersion.finalRules);
        setOriginalValues({
          identity: selectedVersion.identity,
          issueSummary: selectedVersion.issueSummary,
          classification: selectedVersion.classification,
          appealLetter: selectedVersion.appealLetter,
          reprocessLetter: selectedVersion.reprocessLetter || "",
          finalRules: selectedVersion.finalRules,
        });
        setIsEditMode(false);
      }
    } else if (activePrompt) {
      // Load active version for editing
      setIdentity(activePrompt.identity);
      setIssueSummary(activePrompt.issueSummary);
      setClassification(activePrompt.classification);
      setAppealLetter(activePrompt.appealLetter);
      setReprocessLetter(activePrompt.reprocessLetter || "");
      setFinalRules(activePrompt.finalRules);
      setOriginalValues({
        identity: activePrompt.identity,
        issueSummary: activePrompt.issueSummary,
        classification: activePrompt.classification,
        appealLetter: activePrompt.appealLetter,
        reprocessLetter: activePrompt.reprocessLetter || "",
        finalRules: activePrompt.finalRules,
      });
      setIsEditMode(true);
    }
  }, [activePrompt, selectedVersionId, allVersions]);

  // Check if all sections are non-empty (for button enable/disable)
  const isValid =
    identity.trim().length > 0 &&
    issueSummary.trim().length > 0 &&
    classification.trim().length > 0 &&
    appealLetter.trim().length > 0 &&
    finalRules.trim().length > 0;

  // Check if any changes have been made
  const hasChanges =
    identity !== originalValues.identity ||
    issueSummary !== originalValues.issueSummary ||
    classification !== originalValues.classification ||
    appealLetter !== originalValues.appealLetter ||
    reprocessLetter !== originalValues.reprocessLetter ||
    finalRules !== originalValues.finalRules;

  // Handler for switching to edit mode
  const handleEditActiveVersion = () => {
    setSelectedVersionId(null);
    setIsEditMode(true);
  };

  // Handler for version selection
  const handleVersionChange = (e: { value: string | null }) => {
    setSelectedVersionId(e.value);
  };

  // Prepare dropdown options - exclude active version from historical versions list
  const versionOptions = allVersions
    .filter((version) => version.id !== activePrompt?.id)
    .map((version) => ({
      label: `Version ${version.version} (${new Date(
        version.createdAt
      ).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })})`,
      value: version.id,
    }));

  // Handler for saving new version
  const handleSaveNewVersion = async () => {
    if (!isValid) {
      toastRef.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "All five prompt sections must be non-empty",
        life: 5000,
      });
      return;
    }

    try {
      const result = await createPromptVersion({
        identity,
        issueSummary,
        classification,
        appealLetter,
        reprocessLetter,
        finalRules,
      }).unwrap();

      // Update original values to match the newly saved version
      setOriginalValues({
        identity,
        issueSummary,
        classification,
        appealLetter,
        reprocessLetter,
        finalRules,
      });

      toastRef.current?.show({
        severity: "success",
        summary: "Success",
        detail: `Prompt version ${result.version} created successfully`,
        life: 5000,
      });
    } catch (err: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: err?.data?.error || "Failed to create prompt version",
        life: 5000,
      });
    }
  };

  if (isLoading || isLoadingVersions) {
    return (
      <div className="flex justify-content-center align-items-center p-5">
        <ProgressSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3">
        <Message
          severity="error"
          text="Failed to load AI prompt. Please try again."
        />
      </div>
    );
  }

  if (!activePrompt) {
    return (
      <div className="p-3">
        <Message
          severity="warn"
          text="No prompt versions found. Please contact support."
        />
      </div>
    );
  }

  return (
    <div className="ai-prompt-editor p-3">
      <Toast ref={toastRef} />

      {/* Schema Reference - Collapsible Section */}
      <div className="mb-4">
        <Accordion>
          <AccordionTab header="📋 Schema Reference - View Input & Output Schemas">
            <div className="grid">
              <div className="col-12 lg:col-6">
                <h4 className="mt-0">Input Schema (Data Sent TO AI)</h4>
                <pre
                  style={{
                    fontFamily: "monospace",
                    fontSize: "11px",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    padding: "1rem",
                    overflow: "auto",
                    maxHeight: "400px",
                  }}
                >
                  {INPUT_SCHEMA}
                </pre>
              </div>
              <div className="col-12 lg:col-6">
                <h4 className="mt-0">Output Schema (JSON Expected FROM AI)</h4>
                <pre
                  style={{
                    fontFamily: "monospace",
                    fontSize: "11px",
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    padding: "1rem",
                    overflow: "auto",
                    maxHeight: "400px",
                  }}
                >
                  {OUTPUT_SCHEMA}
                </pre>
              </div>
            </div>
          </AccordionTab>
        </Accordion>
      </div>

      {/* Version Selection Dropdown */}
      <div className="mb-4">
        <label htmlFor="version-selector" className="block font-bold mb-2">
          View Historical Version
        </label>
        <div className="flex gap-2 align-items-center">
          <Dropdown
            id="version-selector"
            value={selectedVersionId}
            options={versionOptions}
            onChange={handleVersionChange}
            placeholder="Select a version to view..."
            className="w-full md:w-20rem"
            showClear={selectedVersionId !== null}
          />
          {!isEditMode && (
            <Button
              label="Edit Active Version"
              icon="pi pi-pencil"
              onClick={handleEditActiveVersion}
              className="p-button-outlined"
            />
          )}
        </div>
      </div>

      {/* Read-only mode info message */}
      {!isEditMode && (
        <div className="mb-4">
          <Message
            severity="info"
            text="Viewing read-only version. Use 'Edit Active Version' to make changes or copy text to create new version."
          />
        </div>
      )}

      {/* Metadata Section */}
      <div className="mb-4 p-3 bg-blue-50 border-round">
        <h3 className="mt-0 mb-2">
          {isEditMode ? "Active Prompt Version" : "Selected Version"}
        </h3>
        <div className="text-sm">
          <strong>Version:</strong>{" "}
          {isEditMode
            ? activePrompt.version
            : allVersions.find((v) => v.id === selectedVersionId)?.version}{" "}
          &nbsp;|&nbsp;
          <strong>Created:</strong>{" "}
          {new Date(
            isEditMode
              ? activePrompt.createdAt
              : allVersions.find((v) => v.id === selectedVersionId)
                  ?.createdAt || ""
          ).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </div>
      </div>

      {/* Identity Section */}
      <div className="mb-4">
        <label htmlFor="identity" className="block font-bold mb-2">
          1. Identity / Role Description
        </label>
        <InputTextarea
          id="identity"
          value={identity}
          onChange={(e) => setIdentity(e.target.value)}
          rows={3}
          className="w-full"
          placeholder="Enter the role/identity description..."
          style={{
            fontFamily: "monospace",
            fontSize: "13px",
            backgroundColor: !isEditMode ? "#f8f9fa" : undefined,
            cursor: !isEditMode ? "default" : undefined,
          }}
          readOnly={!isEditMode}
        />
      </div>

      {/* Issue Summary Section */}
      <div className="mb-4">
        <label htmlFor="issue-summary" className="block font-bold mb-2">
          2. Issue Detailed Summary
        </label>
        <InputTextarea
          id="issue-summary"
          value={issueSummary}
          onChange={(e) => setIssueSummary(e.target.value)}
          rows={30}
          className="w-full"
          placeholder="Enter the issue summary section..."
          style={{
            fontFamily: "monospace",
            fontSize: "13px",
            backgroundColor: !isEditMode ? "#f8f9fa" : undefined,
            cursor: !isEditMode ? "default" : undefined,
          }}
          readOnly={!isEditMode}
        />
      </div>

      {/* Classification Section */}
      <div className="mb-4">
        <label htmlFor="classification" className="block font-bold mb-2">
          3. Classification
        </label>
        <InputTextarea
          id="classification"
          value={classification}
          onChange={(e) => setClassification(e.target.value)}
          rows={40}
          className="w-full"
          placeholder="Enter the classification section..."
          style={{
            fontFamily: "monospace",
            fontSize: "13px",
            backgroundColor: !isEditMode ? "#f8f9fa" : undefined,
            cursor: !isEditMode ? "default" : undefined,
          }}
          readOnly={!isEditMode}
        />
      </div>

      {/* Appeal Letter Section */}
      <div className="mb-4">
        <label htmlFor="appeal-letter" className="block font-bold mb-2">
          4. Appeal Letter Template
        </label>
        <InputTextarea
          id="appeal-letter"
          value={appealLetter}
          onChange={(e) => setAppealLetter(e.target.value)}
          rows={30}
          className="w-full"
          placeholder="Enter the appeal letter section..."
          style={{
            fontFamily: "monospace",
            fontSize: "13px",
            backgroundColor: !isEditMode ? "#f8f9fa" : undefined,
            cursor: !isEditMode ? "default" : undefined,
          }}
          readOnly={!isEditMode}
        />
      </div>

      {/* Reprocess Letter Section */}
      <div className="mb-4">
        <label htmlFor="reprocess-letter" className="block font-bold mb-2">
          5. Reprocess Letter Template
          <span className="text-sm font-normal text-600 ml-2">
            (Optional - used when AI classifies claim as reprocess)
          </span>
        </label>
        <InputTextarea
          id="reprocess-letter"
          value={reprocessLetter}
          onChange={(e) => setReprocessLetter(e.target.value)}
          rows={20}
          className="w-full"
          placeholder="Enter the reprocess letter template... (optional)"
          style={{
            fontFamily: "monospace",
            fontSize: "13px",
            backgroundColor: !isEditMode ? "#f8f9fa" : undefined,
            cursor: !isEditMode ? "default" : undefined,
          }}
          readOnly={!isEditMode}
        />
      </div>

      {/* Final Rules Section */}
      <div className="mb-4">
        <label htmlFor="final-rules" className="block font-bold mb-2">
          6. Final Rules
          <span className="text-sm font-normal text-600 ml-2">
            (Final rules and closing instructions)
          </span>
        </label>
        <InputTextarea
          id="final-rules"
          value={finalRules}
          onChange={(e) => setFinalRules(e.target.value)}
          rows={15}
          className="w-full"
          placeholder="Enter the final rules section..."
          style={{
            fontFamily: "monospace",
            fontSize: "13px",
            backgroundColor: !isEditMode ? "#f8f9fa" : undefined,
            cursor: !isEditMode ? "default" : undefined,
          }}
          readOnly={!isEditMode}
        />
      </div>

      {/* Save Button Section - Only visible in edit mode */}
      {isEditMode && (
        <div className="flex justify-content-end gap-2 pt-3 border-top-1 border-200">
          <Button
            label="Save New Version"
            icon="pi pi-save"
            onClick={handleSaveNewVersion}
            disabled={!isValid || !hasChanges || isSaving}
            loading={isSaving}
            className="p-button-success px-5"
          />
        </div>
      )}
    </div>
  );
}

export default AIPromptEditor;
